import RequiredAsterisk from 'app/components/RequiredAsterisk';
import { validationErrorToFormError } from 'app/ReactUseForm/ErrorValidation';
import React from 'react';
import { useController, UseControllerProps } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Form, Input, Label } from 'semantic-ui-react';

type LabeledInputProps = Omit<React.ComponentProps<typeof Input>, 'label'>;

type ControlledLabeledInputProps<ControlledState> = UseControllerProps<ControlledState> &
  LabeledInputProps & { label: string | React.ReactNode };

export function LabeledInput({
  labelInput,
  required,
  ...props
}: LabeledInputProps & { labelInput: string | React.ReactNode }) {
  return (
    <Input
      {...props}
      label={
        <Label>
          {labelInput}
          {required && <RequiredAsterisk />}
        </Label>
      }
    />
  );
}

export default function ControlledLabeledInput<ControlledState>({
  control,
  name,
  rules,
  defaultValue,
  shouldUnregister,
  disabled,
  label,
  ...inputProps
}: ControlledLabeledInputProps<ControlledState>) {
  const intl = useIntl();
  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
    disabled,
  });

  const error = validationErrorToFormError(fieldState.error, rules, intl);

  return (
    <Form.Field
      {...inputProps}
      {...rules}
      as="label"
      error={error}
      control={LabeledInput}
      required={!!rules?.required}
      value={field.value}
      onBlur={field.onBlur}
      onChange={
        ((e, { value }) =>
          field.onChange(
            inputProps.type === 'number' ? Number(value) : value
          )) as React.ComponentProps<typeof Input>['onChange']
      }
      name={field.name}
      disabled={field.disabled}
      input={<input ref={field.ref} type={inputProps.type} />}
      labelInput={label}
    />
  );
}
